import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import axios from 'axios';
import InactivityTimer from './InactivityTimer';

const AppointmentPaymentSuccess = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [himsStatus, setHimsStatus] = useState('pending'); // pending, success, failed

    useEffect(() => {
        const fetchData = async () => {
            const params = new URLSearchParams(location.search);
            const appointmentId = params.get('appointmentId');
            const patientId = params.get('patientId');
            const mrno = params.get('mrno');
            const paymentId = params.get('paymentId');

            try {
                let appointmentData = null;
                let patientData = null;
                let paymentData = null;

                // Fetch appointment details if appointmentId is available
                if (appointmentId) {
                    const appointmentResponse = await axiosInstance.get(`/api/patient/get-appointment-data/${appointmentId}`);
                    appointmentData = appointmentResponse.data;
                }

                // Fetch patient data if patientId is available
                if (patientId) {
                    const patientResponse = await axiosInstance.get(`/api/patient/get-patient-data/${patientId}`);
                    patientData = patientResponse.data;
                }

                // Fetch payment details if paymentId is available
                if (paymentId) {
                    try {
                        const paymentResponse = await axiosInstance.get(`/api/patient/get-payment-details/${appointmentId}`);
                        paymentData = paymentResponse.data;
                        
                        // Parse appointmentMetadata to get doctorId
                        if (paymentData.appointmentMetadata) {
                            try {
                                const metadata = JSON.parse(paymentData.appointmentMetadata);
                                paymentData.parsedMetadata = metadata;
                            } catch (parseError) {
                                console.error('Error parsing appointmentMetadata:', parseError);
                            }
                        }
                    } catch (paymentError) {
                        console.error('Error fetching payment details:', paymentError);
                        // Payment details are optional, continue without them
                    }
                } else if(appointmentId) {
                    // If paymentId is not provided, try to get payment details using appointmentId
                    try {
                        const paymentResponse = await axiosInstance.get(`/api/patient/get-payment-details/${appointmentId}`);
                        paymentData = paymentResponse.data;

                        // Parse appointmentMetadata to get doctorId
                        if (paymentData.appointmentMetadata) {
                            try {
                                const metadata = JSON.parse(paymentData.appointmentMetadata);
                                paymentData.parsedMetadata = metadata;
                            } catch (parseError) {
                                console.error('Error parsing appointmentMetadata:', parseError);
                            }
                        }
                    } catch (paymentError) {
                        console.error('Error fetching payment details:', paymentError);
                        // Payment details are optional, continue without them
                    }
                }

                const finalMrno = mrno || appointmentData?.NewPatient?.mrno || patientData?.patient?.mrno || patientData?.mrno || 'N/A';
                const finalAppointmentDate = appointmentData?.date || 'N/A';
                const finalAppointmentSlot = appointmentData?.slot || 'N/A';
                const departmentId = paymentData?.departmentId || paymentData?.parsedMetadata?.departmentId || 0;
                const doctorId = paymentData?.parsedMetadata?.doctorId || 0;

                // Set data for appointment success
                setData({
                    appointmentId: appointmentId,
                    patientName: `${patientData?.patient?.prefix || patientData?.prefix || ''} ${patientData?.patient?.firstName || patientData?.firstName || ''} ${patientData?.patient?.middleName || patientData?.middleName || ''} ${patientData?.patient?.lastName || patientData?.lastName || ''}`.trim() || 'N/A',
                    patientMrno: finalMrno,
                    doctorName: appointmentData?.doctor || paymentData?.doctorName || 'N/A',
                    department: appointmentData?.department || paymentData?.department || 'N/A',
                    appointmentDate: finalAppointmentDate,
                    appointmentSlot: finalAppointmentSlot,
                    appointmentStatus: appointmentData?.status || 'Confirmed',
                    orderId: paymentData?.razorpayOrderId || 'N/A',
                    paymentId: paymentData?.razorpayPaymentId || paymentId || 'N/A',
                    amount: paymentData?.amount_received ? (paymentData.amount_received) : '0',
                    patientId: patientId,
                    departmentId: departmentId,
                });

                // After all 3 APIs are successful, call HIMS appointment save API
                if (appointmentData && patientData && finalMrno !== 'N/A' && finalAppointmentDate !== 'N/A') {
                    await saveAppointmentToHIMS(finalMrno, finalAppointmentDate, finalAppointmentSlot, departmentId, doctorId);
                    
                    // Create receipt if payment amount is greater than 0
                    if (paymentData && paymentData.amount_received > 0) {
                        const departmentName = appointmentData?.department || paymentData?.department || 'ORTHOPAEDICS';
                        await createReceipt(finalMrno, paymentData, departmentName);
                    }
                }

            } catch (error) {
                console.error('Error fetching appointment data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [location]);

    const saveAppointmentToHIMS = async (mrno, appointmentDate, appointmentSlot, departmentId, doctorId) => {
        try {
            setHimsStatus('processing');
            
            // Format the date and time: yyyy-MM-dd HH:mm (24-hour format, no AM/PM)
            // appointmentDate is in format "06-11-2025" (dd-MM-yyyy)
            const [slotStart] = appointmentSlot.split(' - '); // Get start time only (e.g., "09:00")
            
            // Remove any existing AM/PM from slot if present
            const cleanSlot = slotStart.replace(/\s*(AM|PM|am|pm)\s*$/i, '').trim();
            
            // Convert date from dd-MM-yyyy to yyyy-MM-dd
            const [day, month, year] = appointmentDate.split('-');
            const convertedDate = `${year}-${month}-${day}`; // Result: "2025-11-06"
            
            const formattedDateTime = `${convertedDate} ${cleanSlot}`; // Result: "2025-11-06 09:00"
            
            // Create the 'to' object
            const toParam = {
                loginId: "admin",
                participantId: doctorId,
                department: departmentId,
                branchId: localStorage.getItem('site_id') ? parseInt(localStorage.getItem('site_id')) : 2468
            };
            
            // console.log('Saving appointment to HIMS with data:', {
            //     mrno,
            //     dateTime: formattedDateTime,
            //     departmentId,
            //     doctorId,
            //     toParam
            // });

            const token = localStorage.getItem('token');
            
            // Build URL with proper encoding
            // We need to use axios with custom serializer to prevent automatic URL encoding
            // const baseUrl = `hims-api/test/portal/happ/appointments/save`;
            // const baseUrl = `hims-api/live/portal/happ/appointments/save`;
            const baseUrl = `hims-api/live/portal/happ/appointments/save`;
            
            // console.log('HIMS API call params:', {
            //     mrno,
            //     formattedDateTime,
            //     toParam
            // });
            
            const himsResponse = await axios({
                method: 'POST',
                url: baseUrl,
                params: {
                    patientMRN: mrno,
                    eventDate: formattedDateTime,
                    to: JSON.stringify(toParam)
                },
                paramsSerializer: (params) => {
                    // Custom serializer to prevent encoding of spaces and quotes
                    return Object.keys(params)
                        .map(key => `${key}=${params[key]}`)
                        .join('&');
                },
                headers: {
                    'Authorization': token ? `Bearer ${token}` : '',
                    'Content-Type': 'application/json',
                }
            });

            console.log('HIMS appointment save response:', himsResponse.data);
            
            if (himsResponse.data && himsResponse.data.success !== false) {
                setHimsStatus('success');
                console.log('Appointment saved to HIMS successfully');
            } else {
                setHimsStatus('failed');
                console.warn('HIMS appointment save returned unsuccessful response');
            }
        } catch (himsError) {
            setHimsStatus('failed');
            console.error('Failed to save appointment to HIMS:', himsError);
            console.error('Error response:', himsError.response?.data);
        }
    };

    const createReceipt = async (patientMrno, paymentData, departmentName) => {
        try {
            const token = localStorage.getItem('token');

            if (!token) {
                console.error('Token not found');
                return;
            }

            const receiptAmount = parseFloat(paymentData.amount_received) || 0;
            const referenceId = paymentData.razorpayOrderId || '123';

            // Fetch patient details to get patientId
            const patientResponse = await fetch(
                `${process.env.REACT_APP_KIOSK_OPS_URL}/patient/registeredPatientDetails?input=${encodeURIComponent(patientMrno)}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!patientResponse.ok) {
                console.error('Failed to fetch patient details for receipt');
                return;
            }

            const patientDetailsData = await patientResponse.json();
            console.log('Patient details for receipt:', patientDetailsData);

            // Extract patientId from response
            const patientId = patientDetailsData?.data?.[0]?.patientId || patientDetailsData?.patientId || patientDetailsData?.id;

            if (!patientId) {
                console.error('Patient ID not found in response for receipt creation');
                return;
            }

            const apiUrl = `${process.env.REACT_APP_KIOSK_OPS_URL}/receipt/createNewReceipt?paymentsubtype=RAZORPAY&srcDocId=9&referenceId=${referenceId}&departmentName=${encodeURIComponent(departmentName)}`;

            const requestBody = {
                receiptAmount: receiptAmount,
                depositPatient: {
                    patientId: parseInt(patientId)
                },
                billingSite: {
                    siteId: localStorage.getItem('site_id') ? parseInt(localStorage.getItem('site_id')) : 2468
                },
                createdBy: 2
            };

            console.log('Creating receipt for appointment with:', requestBody);

            const receiptResponse = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(requestBody)
            });

            if (receiptResponse.ok) {
                const receiptData = await receiptResponse.json();
                console.log('Receipt created successfully for appointment:', receiptData);
            } else {
                const errorData = await receiptResponse.text();
                console.error('Failed to create receipt for appointment:', errorData);
            }
        } catch (error) {
            console.error('Error creating receipt for appointment:', error);
        }
    };

    // const createHIMSInvoice = async (paymentData, patientData, appointmentData) => {
    //     try {
    //         console.log('Creating HIMS invoice with data:', {
    //             paymentData,
    //             patientData,
    //             appointmentData
    //         });

    //         // Parse appointment metadata to get service details
    //         const metadata = paymentData.parsedMetadata || JSON.parse(paymentData.appointmentMetadata || '{}');
            
    //         const token = localStorage.getItem('token');
            
    //         // Prepare invoice payload
    //         const invoicePayload = {
    //             serviceList: [
    //                 {
    //                     serviceId: metadata.serviceId || 16109,
    //                     quantity: 1
    //                 }
    //             ],
    //             consultantId: String(metadata.doctorId || paymentData.consultantId || "4291"),
    //             patientId: patientData.patient?.externalPatientId || patientData.externalPatientId || 3870337,
    //             departmentId: String(metadata.departmentId || paymentData.departmentId || "7264587"),
    //             siteId: String(metadata.hospitalId || "2468"),
    //             payment: {
    //                 paymentId: paymentData.razorpayPaymentId,
    //                 amount: paymentData.amount_received,
    //                 currency: paymentData.currency || "INR",
    //                 orderId: paymentData.razorpayOrderId,
    //                 status: "SUCCESS"
    //             }
    //         };

    //         console.log('HIMS Invoice API payload:', invoicePayload);

    //         const invoiceResponse = await axios.post(
    //             `${process.env.REACT_APP_KIOSK_OPS_URL}/invoice/create`,
    //             invoicePayload,
    //             {
    //                 headers: {
    //                     'Authorization': token ? `Bearer ${token}` : '',
    //                     'Content-Type': 'application/json',
    //                 }
    //             }
    //         );

    //         console.log('HIMS invoice creation response:', invoiceResponse.data);
            
    //         if (invoiceResponse.data) {
    //             console.log('Invoice created in HIMS successfully');
    //         }
    //     } catch (invoiceError) {
    //         console.error('Failed to create invoice in HIMS:', invoiceError);
    //         console.error('Invoice error response:', invoiceError.response?.data);
    //         // Don't fail the entire flow if invoice creation fails
    //     }
    // };

    if (loading) {
        return (
            <div className="fixed inset-0 z-50 flex justify-center items-center h-full bg-black bg-opacity-50">
                <div className="bg-white p-8 rounded-lg">
                    <div className="text-center">Loading...</div>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="fixed inset-0 z-50 flex justify-center items-center h-full bg-black bg-opacity-50">
                <div className="bg-white p-8 rounded-lg">
                    <div className="text-center">No data available</div>
                </div>
            </div>
        );
    }

    return (
        <>
            <InactivityTimer />
            <div className="fixed inset-0 z-50 flex justify-center h-full bg-black bg-opacity-50 overflow-y-auto p-2 sm:p-4">
                <div className="relative my-auto p-4 sm:p-8 w-full max-w-[95vw] bg-white rounded-2xl shadow-2xl">
                    <div className="text-center">
                        <svg className="w-12 h-12 sm:w-16 sm:h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3">Appointment Booked Successfully!</h2>
                        <p className="text-gray-600 mb-6">Your appointment has been confirmed.</p>

                        {/* Appointment Details Section */}
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-8 mb-6 sm:mb-8">
                            {/* Left Side - Patient & Appointment Details */}
                            <div className="bg-gray-50 p-4 sm:p-8 rounded-xl">
                                <h3 className="text-xl font-bold text-gray-800 mb-4">
                                    Patient Details
                                </h3>
                                <div className="space-y-3 text-sm sm:text-base text-left break-words">
                                    <p><strong>Patient Name:</strong> {data.patientName}</p>
                                    <p><strong>MR Number:</strong> {data.patientMrno}</p>
                                    <p><strong>Appointment ID:</strong> {data.appointmentId}</p>
                                </div>
                            </div>

                            {/* Right Side - Appointment Information */}
                            <div className="bg-blue-50 p-4 sm:p-8 rounded-xl border border-blue-200">
                                <h3 className="text-xl font-bold text-gray-800 mb-4">
                                    Appointment Information
                                </h3>
                                <div className="space-y-3 text-sm sm:text-base text-left break-words">
                                    <p><strong>Doctor:</strong> {data.doctorName}</p>
                                    <p><strong>Department:</strong> {data.department}</p>
                                    <p><strong>Date:</strong> {data.appointmentDate}</p>
                                    <p><strong>Time Slot:</strong> {data.appointmentSlot}</p>
                                    <p>
                                        <strong>Status:</strong> 
                                        <span className="ml-2 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            {data.appointmentStatus}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Payment Details Section */}
                        <div className="bg-green-50 p-4 sm:p-6 rounded-xl border border-green-200 mb-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">Payment Details</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-base text-left">
                                <div>
                                    <p className="text-gray-600">Order ID</p>
                                    <p className="font-semibold text-gray-800 break-all">{data.orderId}</p>
                                </div>
                                <div>
                                    <p className="text-gray-600">Payment ID</p>
                                    <p className="font-semibold text-gray-800 break-all">{data.paymentId}</p>
                                </div>
                                <div>
                                    <p className="text-gray-600">Amount Paid</p>
                                    <p className="font-semibold text-green-600 text-xl">₹{data.amount}</p>
                                </div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-green-300">
                                <p className="text-sm">
                                    <strong>HIMS Sync Status:</strong>
                                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                                        himsStatus === 'success' 
                                            ? 'bg-green-100 text-green-800' 
                                            : himsStatus === 'failed'
                                            ? 'bg-red-100 text-red-800'
                                            : himsStatus === 'processing'
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                        {himsStatus === 'success' 
                                            ? 'Synced' 
                                            : himsStatus === 'failed'
                                            ? 'Sync Failed'
                                            : himsStatus === 'processing'
                                            ? 'Syncing...'
                                            : 'Pending'}
                                    </span>
                                </p>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-center gap-4 mt-8">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="w-full sm:w-auto px-8 py-3 bg-blue-600 text-white font-semibold text-base sm:text-lg rounded-xl shadow-lg hover:bg-blue-700 transition duration-200"
                            >
                                Go to Dashboard
                            </button>
                            {/* <button
                                onClick={() => navigate('/')}
                                className="px-8 py-3 bg-[#0FA968] text-white font-semibold text-lg rounded-xl shadow-lg hover:bg-[#138F5E] transition duration-200"
                            >
                                Back to Home
                            </button> */}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AppointmentPaymentSuccess;
